// src/contexts/MeContext.tsx
import React, {
  createContext,
  useCallback,
  useContext,
  useEffect,
  useState,
  ReactNode,
} from "react";

export type MeUser = {
  id: number | string;
  lineName?: string;
  points?: number;
  // 必要に応じて email / roles など後で足せる
};

type MeContextValue = {
  me: MeUser | null;
  loading: boolean;
  error: string | null;
  refresh: () => Promise<void>;
};

const MeContext = createContext<MeContextValue | undefined>(undefined);

type MeProviderProps = {
  children: ReactNode;
};

export const MeProvider: React.FC<MeProviderProps> = ({ children }) => {
  const [me, setMe] = useState<MeUser | null>(null);
  const [loading, setLoading] = useState<boolean>(true);
  const [error, setError] = useState<string | null>(null);

  const fetchMe = useCallback(async () => {
    try {
      setLoading(true);
      setError(null);

      const res = await fetch("/api/auth/me", {
        credentials: "include",
      });

      if (!res.ok) {
        throw new Error(`status ${res.status}`);
      }

      const data = await res.json(); // { ok: true, user: {...} } を想定
      setMe(data.user ?? null);
    } catch (e: any) {
      console.error("Failed to fetch /api/auth/me", e);
      setError(e?.message ?? "failed to fetch /api/auth/me");
      setMe(null);
    } finally {
      setLoading(false);
    }
  }, []);

  useEffect(() => {
    // マウント時に一度だけ取得
    fetchMe();
  }, [fetchMe]);

  const value: MeContextValue = {
    me,
    loading,
    error,
    refresh: fetchMe,
  };

  return <MeContext.Provider value={value}>{children}</MeContext.Provider>;
};

export const useMe = (): MeContextValue => {
  const ctx = useContext(MeContext);
  if (!ctx) {
    throw new Error("useMe must be used within a MeProvider");
  }
  return ctx;
};
